import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr'; 
import './style/CreateCampaign.css';

// --- Custom Select Component ---
const CustomSelect = ({ options, value, onChange, placeholder, labelKey = 'name', valueKey = 'id', imageKey = 'logo' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedItem = options.find(o => o[valueKey] === value);

    return (
        <div className={`cc-select-wrapper ${isOpen ? 'open' : ''}`} ref={containerRef}>
            <div className={`cc-select-toggle ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                {selectedItem ? (
                    <div className="cc-sel-display">
                        {imageKey && selectedItem[imageKey] && <img src={selectedItem[imageKey]} alt="" className="cc-mini-icon" />}
                        <span>{selectedItem[labelKey]}</span>
                    </div>
                ) : (
                    <span className="cc-placeholder">{placeholder}</span>
                )}
                <span className="cc-arrow-icon">▼</span>
            </div>
            {isOpen && (
                <div className="cc-select-menu">
                    {options.map((opt) => (
                        <div
                            key={opt[valueKey]}
                            className={`cc-select-item ${value === opt[valueKey] ? 'selected' : ''}`}
                            onClick={() => { onChange(opt[valueKey]); setIsOpen(false); }}
                        >
                            {imageKey && opt[imageKey] && <img src={opt[imageKey]} alt="" className="cc-mini-icon" />}
                            <span>{opt[labelKey]}</span>
                            {opt.payout && <span className="cc-payout-badge">₹{opt.payout}</span>}
                        </div>
                    ))}
                    {options.length === 0 && <div className="cc-select-empty">No options available</div>}
                </div>
            )}
        </div>
    );
};

// --- Step Tag Input Component ---
const StepInput = ({ steps, setSteps }) => {
    const [inputVal, setInputVal] = useState('');
    const typingTimeoutRef = useRef(null);

    const addStep = (val) => {
        const trimmed = val.trim();
        if (trimmed && !steps.includes(trimmed)) {
            setSteps([...steps, trimmed]);
        }
        setInputVal('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addStep(inputVal);
        }
    };

    const handleChange = (e) => {
        const val = e.target.value;
        setInputVal(val);

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        
        if (val.trim()) {
            typingTimeoutRef.current = setTimeout(() => {
                addStep(val);
            }, 2000); 
        }
    };

    const removeStep = (idx) => {
        setSteps(steps.filter((_, i) => i !== idx));
    };

    return (
        <div className="cc-step-container">
            <div className="cc-tags-area">
                {steps.map((step, idx) => (
                    <span key={idx} className="cc-step-tag">
                        {step}
                        <button onClick={() => removeStep(idx)}>&times;</button>
                    </span>
                ))}
            </div>
            <input 
                type="text" 
                className="cc-glass-input" 
                placeholder="Type step and press Enter or wait..." 
                value={inputVal}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
            />
        </div>
    );
};

// --- Main Component ---
const CreateCampaign = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    // Data State
    const [loading, setLoading] = useState(true);
    const [offers, setOffers] = useState([]);
    const [domains, setDomains] = useState([]);
    
    // Form State
    const [selectedOfferId, setSelectedOfferId] = useState('');
    const [eventPayouts, setEventPayouts] = useState({}); 
    
    const [campDetails, setCampDetails] = useState({
        title: '',
        name: '',
        telegram_link: '',
        steps: []
    });

    const [referDetails, setReferDetails] = useState({
        title: '',
        name: ''
    });

    const [selectedDomain, setSelectedDomain] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- 1. Fetch Resources ---
    useEffect(() => {
                const authToken = getCookie('authToken');
        const fetchData = async () => {
            try {
                setLoading(true);
                const userResponse = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuth',
                    authToken
                });

                if (userResponse.data.status === false) {
                    toast.error(userResponse.data.message || 'Session expired', 3000);
                    navigate('/login');

                    return;
                } else {

                    setUser(userResponse.data.res);
                    await initData();
                }
            } catch (error) {
                console.error(error);
                toast.error('Failed to load dashboard data');
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const initData = async () => {
            const authToken = getCookie('authToken');
            try {
                const offerRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'fetchPublisherOffers',
                    authToken
                });
                
                const domainRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'fetchDomains',
                    authToken
                });

                if (offerRes.data.status) setOffers(offerRes.data.offers);
                else setOffers([]); 

                if (domainRes.data.status) setDomains(domainRes.data.domains);
                
            } catch (error) {
                toast.error("Failed to load resources");
            } finally {
                setLoading(false);
            }
        };
        
    }, []);

    // --- 2. Handle Offer Selection ---
    useEffect(() => {
        if (!selectedOfferId) {
            setEventPayouts({});
            return;
        }

        const offer = offers.find(o => o.id === selectedOfferId);
        if (offer && offer.events) {
            const initialPayouts = {};
            offer.events.forEach(evt => {
                initialPayouts[evt.id] = { user: '', refer: '' };
            });
            setEventPayouts(initialPayouts);
            if(!campDetails.title) setCampDetails(prev => ({ ...prev, title: offer.name, name: offer.name }));
        }
    }, [selectedOfferId, offers]);

    // --- 3. Form Handlers ---
    const handlePayoutChange = (evtId, field, val) => {
        const numericVal = parseFloat(val) || 0;
        if (numericVal < 0) return; // No negatives

        // Find the event limit
        const activeOffer = offers.find(o => o.id === selectedOfferId);
        const eventObj = activeOffer?.events.find(e => e.id === evtId);
        const totalLimit = parseFloat(eventObj?.payout || 0);

        // Calculate projected total
        const currentEntry = eventPayouts[evtId] || { user: '', refer: '' };
        const otherField = field === 'user' ? 'refer' : 'user';
        const otherVal = parseFloat(currentEntry[otherField]) || 0;

        if (numericVal + otherVal > totalLimit) {
            toast.warning(`Total payout cannot exceed ₹${totalLimit}`);
            return; // Block the update
        }

        setEventPayouts(prev => ({
            ...prev,
            [evtId]: { ...prev[evtId], [field]: val }
        }));
    };

    const handleSubmit = async () => {
        if (!selectedOfferId) return toast.warning("Please select an offer");
        if (!selectedDomain) return toast.warning("Please select a domain");
        if (campDetails.steps.length === 0) return toast.warning("Add at least one step");

        setIsSubmitting(true);
        const authToken = getCookie('authToken');

        try {
            const payload = {
                action: 'createCampaign',
                authToken,
                offer_id: selectedOfferId,
                domain_id: selectedDomain,
                campaign_meta: campDetails,
                refer_meta: referDetails,
                payout_config: eventPayouts
            };

            const res = await axios.post('https://publisher.cashbolt.in.net/server/', payload);

            if (res.data.status) {
                toast.success("Campaign Created Successfully!");
                navigate('/campaigns/list');
            } else {
                toast.error(res.data.message || "Creation Failed");
            }
        } catch (err) {
            toast.error("Server Error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <Loader />;

    const activeOffer = offers.find(o => o.id === selectedOfferId);

    return (
        <div className="cc-wrapper">
            <Navbar name={user?.name || 'Partner'} role={user?.role || 'Publisher'} />
            <div className="cc-bg-orb cc-orb-1"></div>
            <div className="cc-bg-orb cc-orb-2"></div>

            <div className="cc-container">
                
                <div className="cc-header">
                    <h2>Create <span className="cc-highlight">Campaign</span></h2>
                    <p>Setup your tracking page and payouts</p>
                </div>

                {/* --- 1. Offer Selection --- */}
                <div className="cc-card">
                    <h3 className="cc-card-title">Select Offer</h3>
                    <CustomSelect 
                        options={offers} 
                        value={selectedOfferId} 
                        onChange={setSelectedOfferId} 
                        placeholder="Choose an Offer..."
                    />

                    {/* --- Payout Configuration --- */}
                    {activeOffer && activeOffer.events && (
                        <div className="cc-events-section">
                            <h4 className="cc-sub-title">Configure Payouts</h4>
                            <div className="cc-events-grid">
                                {activeOffer.events.map(evt => (
                                    <div key={evt.id} className="cc-event-box">
                                        <div className="cc-evt-header">
                                            <span className="cc-evt-name">{evt.name}</span>
                                            <span className="cc-evt-total">Total: ₹{evt.payout}</span>
                                        </div>
                                        <div className="cc-payout-inputs">
                                            <div className="cc-inp-grp">
                                                <label>User (₹)</label>
                                                <input 
                                                    type="number" 
                                                    placeholder="0" 
                                                    value={eventPayouts[evt.id]?.user || ''}
                                                    onChange={(e) => handlePayoutChange(evt.id, 'user', e.target.value)}
                                                />
                                            </div>
                                            <div className="cc-inp-grp">
                                                <label>Refer (₹)</label>
                                                <input 
                                                    type="number" 
                                                    placeholder="0" 
                                                    value={eventPayouts[evt.id]?.refer || ''}
                                                    onChange={(e) => handlePayoutChange(evt.id, 'refer', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* --- 2. Campaign Page Details --- */}
                <div className="cc-card">
                    <h3 className="cc-card-title">Campaign Page Details</h3>
                    <div className="cc-form-row">
                        <div className="cc-form-group">
                            <label>Internal Name</label>
                            <input 
                                type="text" 
                                className="cc-glass-input" 
                                placeholder="e.g. My Insta Campaign" 
                                value={campDetails.name}
                                onChange={(e) => setCampDetails({...campDetails, name: e.target.value})}
                            />
                        </div>
                        <div className="cc-form-group">
                            <label>Page Title</label>
                            <input 
                                type="text" 
                                className="cc-glass-input" 
                                placeholder="Displayed to user" 
                                value={campDetails.title}
                                onChange={(e) => setCampDetails({...campDetails, title: e.target.value})}
                            />
                        </div>
                    </div>

                    <div className="cc-form-group">
                        <label>Steps (Instructions)</label>
                        <StepInput 
                            steps={campDetails.steps} 
                            setSteps={(s) => setCampDetails({...campDetails, steps: s})} 
                        />
                        <small className="cc-hint">Type and press Enter, Comma, or wait 2s to add a step.</small>
                    </div>

                    <div className="cc-form-group">
                        <label>Telegram Channel Link</label>
                        <input 
                            type="text" 
                            className="cc-glass-input" 
                            placeholder="https://t.me/..." 
                            value={campDetails.telegram_link}
                            onChange={(e) => setCampDetails({...campDetails, telegram_link: e.target.value})}
                        />
                    </div>
                </div>

                {/* --- 3. Refer Page Settings --- */}
                <div className="cc-card">
                    <h3 className="cc-card-title">Refer Page Settings</h3>
                    <div className="cc-form-row">
                        <div className="cc-form-group">
                            <label>Refer Page Name</label>
                            <input 
                                type="text" 
                                className="cc-glass-input" 
                                placeholder="Internal Ref Name" 
                                value={referDetails.name}
                                onChange={(e) => setReferDetails({...referDetails, name: e.target.value})}
                            />
                        </div>
                        <div className="cc-form-group">
                            <label>Refer Page Title</label>
                            <input 
                                type="text" 
                                className="cc-glass-input" 
                                placeholder="Earn ₹500 Daily..." 
                                value={referDetails.title}
                                onChange={(e) => setReferDetails({...referDetails, title: e.target.value})}
                            />
                        </div>
                    </div>
                </div>

                {/* --- 4. Domain & Submit --- */}
                <div className="cc-card">
                    <h3 className="cc-card-title">Launch Configuration</h3>
                    <div className="cc-form-group">
                        <label>Select Domain</label>
                        <CustomSelect 
                            options={domains} 
                            value={selectedDomain} 
                            onChange={setSelectedDomain} 
                            placeholder="Choose a Domain..."
                            labelKey="domain"
                            imageKey={null} 
                        />
                    </div>

                    <button className="cc-submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'CREATING...' : 'CREATE CAMPAIGN'}
                        <div className="cc-wave"></div>
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CreateCampaign;